import { useState, useEffect } from 'react'
import { trackEvent } from '../lib/posthog'

interface HCPGateProps {
  onAccept: () => void
  userId?: string
}

export default function HCPGate({ onAccept, userId }: HCPGateProps) {
  const [startTime] = useState(Date.now())
  const [isChecked, setIsChecked] = useState(false)

  const handleAccept = () => {
    const timeTakenSeconds = Math.round((Date.now() - startTime) / 1000)

    // Track completion
    trackEvent('hcp_gate_completed', {
      user_id: userId,
      time_taken_seconds: timeTakenSeconds
    })

    onAccept()
  }

  // Prevent closing with ESC or clicking outside
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <div className="text-center mb-6">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
            <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Professional Verification</h2>
          <p className="text-gray-600">
            GPMeta is designed exclusively for healthcare professionals
          </p>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <div className="flex">
            <svg className="flex-shrink-0 h-5 w-5 text-amber-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-amber-800">
                Important Notice
              </h3>
              <div className="mt-2 text-sm text-amber-700">
                <ul className="list-disc list-inside space-y-1">
                  <li>This tool is <strong>not for patient use</strong></li>
                  <li>It does <strong>not replace clinical judgment</strong></li>
                  <li>Always verify information against primary sources</li>
                  <li>Not intended as clinical decision support</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <input
              id="hcp-confirmation"
              type="checkbox"
              checked={isChecked}
              onChange={(e) => setIsChecked(e.target.checked)}
              className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="hcp-confirmation" className="text-sm text-gray-700">
              <span className="font-semibold">I confirm that:</span>
              <br />
              • I am a registered healthcare professional in Australia
              <br />
              • I will use this tool only in a professional capacity
              <br />
              • I understand this is not a substitute for clinical judgment
              <br />
              • I will not share access with patients or non-professionals
            </label>
          </div>

          <button
            onClick={handleAccept}
            disabled={!isChecked}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
          >
            Continue to GPMeta
          </button>
        </div>

        <div className="mt-6 text-center text-xs text-gray-500">
          <p>
            By continuing, you agree to our{' '}
            <a href="/terms" className="text-blue-600 hover:underline">Terms of Service</a>
            {' '}and{' '}
            <a href="/privacy" className="text-blue-600 hover:underline">Privacy Policy</a>
          </p>
        </div>
      </div>
    </div>
  )
}